"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/utils";

const DOT_SPACING = 28;
const DOT_RADIUS = 3;

function createRng(initialSeed: number) {
  let seed = initialSeed;
  return () => {
    seed = Math.imul(seed ^ (seed >>> 15), seed | 1);
    seed ^= seed + Math.imul(seed ^ (seed >>> 7), seed | 61);
    return ((seed ^ (seed >>> 14)) >>> 0) / 4294967296;
  };
}

type Dot = {
  x: number;
  y: number;
  col: string;
  base: number;
  phase: number;
};

type Scene = {
  width: number;
  height: number;
  dpr: number;
  dots: Dot[];
  scanC: HTMLCanvasElement;
  vigC: HTMLCanvasElement;
};

function buildScene(
  width: number,
  height: number,
  dpr: number,
  hiddenRatio: number
): Scene {
  const rng = createRng(7331);
  const dots: Dot[] = [];

  for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
    for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
      if (rng() < hiddenRatio) continue;
      dots.push({
        x,
        y,
        col: rng() < 0.3 ? "#00F1BA" : "#938AFF",
        base: 0.45 + rng() * 0.45,
        phase: rng() * Math.PI * 2,
      });
    }
  }

  const mkLayer = () => {
    const el = document.createElement("canvas");
    el.width = Math.round(width * dpr);
    el.height = Math.round(height * dpr);
    return el;
  };

  const scanC = mkLayer();
  const sCtx = scanC.getContext("2d");
  if (sCtx) {
    sCtx.scale(dpr, dpr);
    sCtx.fillStyle = "rgba(0,0,0,0.06)";
    for (let y = 0; y < height; y += 4) {
      sCtx.fillRect(0, y, width, 2);
    }
  }

  const vigC = mkLayer();
  const vCtx = vigC.getContext("2d");
  if (vCtx) {
    vCtx.scale(dpr, dpr);
    const vg = vCtx.createRadialGradient(
      width / 2,
      height / 2,
      height * 0.2,
      width / 2,
      height / 2,
      height * 0.95
    );
    vg.addColorStop(0, "rgba(8,6,14,0)");
    vg.addColorStop(1, "rgba(8,6,14,0.35)");
    vCtx.fillStyle = vg;
    vCtx.fillRect(0, 0, width, height);
  }

  return { width, height, dpr, dots, scanC, vigC };
}

export function DotsEffect({
  className,
  opacity = 0.65,
  hiddenRatio = 0.4,
}: {
  className?: string;
  opacity?: number;
  /** Fraction of dots to omit from the grid (0–1). */
  hiddenRatio?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let scene: Scene | null = null;
    let t0: number | null = null;
    let rafId = 0;
    let visible = true;

    const resize = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width === 0 || height === 0) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      scene = buildScene(width, height, dpr, hiddenRatio);
    };

    const draw = (ts: number) => {
      if (!visible || !scene) return;

      const { width, dpr, dots, scanC, vigC } = scene;

      if (t0 === null) t0 = ts;
      const t = (ts - t0) / 1000;
      const scanX = ((t * 0.15) % 1) * width;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);

      for (const d of dots) {
        const a = Math.max(
          0.12,
          Math.min(
            1,
            d.base +
            Math.sin(t * 2.5 + d.phase) * 0.35 +
            Math.max(0, 1 - Math.abs(d.x - scanX) / 80) * 0.55
          )
        );
        ctx.globalAlpha = a;
        ctx.fillStyle = d.col;
        ctx.beginPath();
        ctx.arc(d.x, d.y, DOT_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(scanC, 0, 0);
      ctx.globalAlpha = opacity * 0.45;
      ctx.drawImage(vigC, 0, 0);
      ctx.globalAlpha = 1;

      rafId = requestAnimationFrame(draw);
    };

    resize();

    const resizeObs = new ResizeObserver(() => {
      resize();
      t0 = null;
    });
    resizeObs.observe(canvas);

    const visObs = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) {
        t0 = null;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(draw);
      } else {
        cancelAnimationFrame(rafId);
      }
    });
    visObs.observe(canvas);

    if (scene) {
      rafId = requestAnimationFrame(draw);
    }

    return () => {
      visible = false;
      cancelAnimationFrame(rafId);
      resizeObs.disconnect();
      visObs.disconnect();
    };
  }, [opacity, hiddenRatio]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 block h-full w-full opacity-20",
        className
      )}
    />
  );
}
